import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiService, { llmBillingApi, webhooksApi } from '../services/api';

const INTEGRATIONS_QUERY_KEY = ['integrations'];
// GitLab multi-instance list query key — declared up here (alongside the
// aggregate key) so both the connect + disconnect hooks below can invalidate
// it without a forward reference.
const GITLAB_INTEGRATIONS_QUERY_KEY = ['gitlab-integrations'];

// Provider IDs whose Connect/Disconnect/OAuth-start routes live on the
// SEPARATE llm-billing RestApi (LlmBillingRoutesNestedStack), not the
// main api-prod RestApi. Status reads are still served by the main
// api-prod RestApi via /integrations/status (aggregated), so this list
// only matters for write mutations + OAuth start.
const LLM_BILLING_PROVIDERS = new Set([
  'openai_billing',
  'anthropic_billing',
  'cursor_admin',
  'notion',
  // Plane connect/status/disconnect routes also live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack). Status is still aggregated via
  // the main /integrations/status endpoint; this only routes mutations.
  'plane',
  // Linear connect/status/disconnect routes also live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack). Same as Plane — status is
  // aggregated via /integrations/status; this only routes mutations.
  'linear',
  // Figma OAuth start/callback/disconnect routes live on the llm-billing
  // RestApi (LlmBillingRoutesNestedStack), same as Notion. Status is still
  // aggregated via the main /integrations/status endpoint; this list routes
  // the OAuth start + disconnect to the right RestApi.
  'figma',
]);

const apiFor = (service) => (LLM_BILLING_PROVIDERS.has(service) ? llmBillingApi : ApiService);

export const useAllIntegrationStatuses = () => {
  return useQuery({
    queryKey: INTEGRATIONS_QUERY_KEY,
    queryFn: () => ApiService.request('/integrations/status'),
  });
};

export const useInvalidateIntegrations = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
};

// Map a service identifier to its REST path prefix. Most providers use
// `/<service>/disconnect`, but the llm-billing trio use hyphen-separated
// prefixes (`/openai-billing/...`) to match the backend handler's path
// dispatch table in handlers/llm-billing.js → PROVIDERS[].pathPrefix.
const DISCONNECT_PATH = {
  openai_billing: '/openai-billing/disconnect',
  anthropic_billing: '/anthropic-billing/disconnect',
  cursor_admin: '/cursor-admin/disconnect',
  notion: '/notion/disconnect',
  plane: '/plane/disconnect',
  linear: '/linear/disconnect',
  figma: '/figma/disconnect',
};

export const useDisconnectIntegration = (service) => {
  const queryClient = useQueryClient();
  const client = apiFor(service);
  const path = DISCONNECT_PATH[service] || `/${service}/disconnect`;

  return useMutation({
    // GitLab is multi-instance: pass { integrationId } to disconnect ONE specific
    // instance (the backend validates ownership + clears any link pins to it).
    // Omit it (or call with no args) for the account-wide disconnect (back-compat).
    mutationFn: (vars) => client.request(path, {
      method: 'POST',
      ...(vars && vars.integrationId
        ? { body: JSON.stringify({ integrationId: vars.integrationId }) }
        : {}),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      // GitLab is multi-instance — also refetch the dedicated list so a
      // disconnect updates the instance count + the modal's instances list.
      if (service === 'gitlab') {
        queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
      }
    },
  });
};

export const useConnectGitlab = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // signingToken is OPTIONAL — the per-webhook `whsec_…` GitLab generates so
    // inbound deliveries can be HMAC-validated per-tenant. The backend
    // (handlers/gitlab.js connectGitlab) accepts + stores it; omit it and the
    // legacy static-token path still works (and a reconnect preserves a prior
    // token). Only send it when non-empty so we never overwrite a stored token
    // with a blank.
    mutationFn: ({ instanceUrl, accessToken, signingToken }) => {
      const body = { instanceUrl, accessToken };
      if (signingToken && signingToken.trim()) body.signingToken = signingToken.trim();
      return ApiService.request('/gitlab/connect', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      // Also refetch the multi-instance list so a newly-added instanceUrl
      // (which ADDS a row, never overwrites a different instanceUrl) shows up.
      queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
    },
  });
};

// ── GitLab INTEGRATIONS (multi-instance) — GET /gitlab/integrations ──────────
// An account may hold MULTIPLE gitlab integrations (e.g. a self-hosted instance
// AND gitlab.com). /integrations/status collapses to one-per-provider and omits
// the integrationId, so it can't drive the multi-instance list or the link
// instance-picker. This endpoint returns EVERY gitlab integration with its
// integrationId + public-safe descriptor fields (NO tokens). It lives on the
// dedicated Webhooks RestApi (same stack as /gitlab/links), NOT the main
// api-prod RestApi — confirmed in backend/src/handlers/gitlab-links.js.
export const useGitlabIntegrations = () => {
  return useQuery({
    queryKey: GITLAB_INTEGRATIONS_QUERY_KEY,
    // → { integrations: [{ integrationId, instanceUrl, username, status, connectedAt }] }
    queryFn: () => webhooksApi.request('/gitlab/integrations'),
  });
};

export const useInvalidateGitlabIntegrations = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: GITLAB_INTEGRATIONS_QUERY_KEY });
};

// ── GitLab repo↔agent LINKS (the /gitlab/links CRUD, on the Webhooks RestApi) ─
// The GitLab analog of /github/links. A link maps a GitLab project/group/path
// to ONE deployed agent (workflow slug) that reviews its MRs. These power the
// repo→agent picker in GitlabConnectModal. They live on webhooksApi (the
// dedicated Webhooks RestApi), NOT the main ApiService.
const gitlabLinksKey = (projectId) => ['gitlab-links', projectId];

export const useGitlabLinks = (projectId) => {
  return useQuery({
    queryKey: gitlabLinksKey(projectId),
    queryFn: () => webhooksApi.request(`/gitlab/links?projectId=${encodeURIComponent(projectId)}`),
    enabled: !!projectId,
  });
};

export const useCreateGitlabLink = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    // body: { kind, identifier, projectId, agentSlug, commentResponseSlug? }
    //   kind ∈ { 'project', 'group', 'path' }
    mutationFn: (body) => webhooksApi.request('/gitlab/links', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabLinksKey(projectId) });
    },
  });
};

export const useDeleteGitlabLink = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    // body: { kind, identifier }
    mutationFn: (body) => webhooksApi.request('/gitlab/links', {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabLinksKey(projectId) });
    },
  });
};

export const useConnectLark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, appSecret, verificationToken, encryptKey }) => {
      return ApiService.request('/lark/connect', {
        method: 'POST',
        body: JSON.stringify({ appId, appSecret, verificationToken, encryptKey })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Shared factory for the three paste-token LLM-billing connectors. They
// all POST { adminKey } to /<provider>/connect on the LlmBilling RestApi
// and refresh the integrations cache on success. Backend validation
// returns a structured error (status 400/401/403 + message) that
// ApiClient.request preserves on err.message + err.status — callers
// surface err.message directly in the modal.
const makeConnectLlmBillingHook = (pathPrefix) => () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adminKey }) => {
      return llmBillingApi.request(`${pathPrefix}/connect`, {
        method: 'POST',
        body: JSON.stringify({ adminKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

export const useConnectOpenAIBilling = makeConnectLlmBillingHook('/openai-billing');
export const useConnectAnthropicBilling = makeConnectLlmBillingHook('/anthropic-billing');
export const useConnectCursorAdmin = makeConnectLlmBillingHook('/cursor-admin');

// Plane — api-key paste-token connector. Unlike the LLM-billing trio it
// posts THREE values { apiKey, workspaceSlug, baseUrl } to /plane/connect
// on the same llm-billing RestApi. baseUrl is optional client-side
// (backend defaults to https://api.plane.so when blank).
export const useConnectPlane = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey, workspaceSlug, baseUrl }) => {
      return llmBillingApi.request('/plane/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey, workspaceSlug, baseUrl }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

// Linear — api-key paste-token connector. Posts a SINGLE value { apiKey }
// to /linear/connect on the same llm-billing RestApi. Simpler than Plane
// (no workspaceSlug/baseUrl). Backend validates the key (Linear viewer
// query) before persisting; structured errors surface on err.message.
export const useConnectLinear = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apiKey }) => {
      return llmBillingApi.request('/linear/connect', {
        method: 'POST',
        body: JSON.stringify({ apiKey }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
};

export const useStartOAuth = (service) => {
  const client = apiFor(service);
  return useMutation({
    // Pass `{ returnTo }` via mutate() to override where the OAuth
    // callback redirects the user after auth. The marketplace detail
    // modal uses this to point popup-OAuth back at
    // `/oauth-popup-done.html` so the popup can close itself and
    // postMessage the opener. Without `returnTo`, the backend's
    // callback falls back to `/integrations?<service>=success` (the
    // canonical path).
    //
    // Notion lives on the llm-billing RestApi (Agent A's stack); all
    // other OAuth services live on the main api-prod RestApi. The
    // client switch above routes the request automatically based on
    // the service id.
    mutationFn: async ({ returnTo } = {}) => {
      const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
      const data = await client.request(`/${service}/oauth/start${qs}`, { method: 'GET' });
      if (!data.authUrl) {
        throw new Error(`Failed to get ${service} OAuth URL`);
      }
      return data;
    },
  });
};
